/**
 * Tipi e aiuti delle viste degli allenamenti: stati con le etichette,
 * raggruppamento dell'elenco (V10), riepilogo delle presenze (V13), media
 * dei voti (V15).
 *
 * I giorni sono quelli italiani, come sul server: "passato" e "oggi" non
 * devono dipendere dal fuso del telefono.
 */

import type {
  ClassificaBlocco,
  RuoloGiocatore,
  EtichettaScostamento,
  StatoAllenamento,
  StatoPresenza,
} from '@calcio/shared';
import { dataLocale } from '@calcio/shared';
import { BandageIcon, CheckIcon, type LucideIcon, XIcon } from 'lucide-react';
import { formatoIstante } from '../comuni/date.js';

export type { StatoAllenamento, StatoPresenza };

export const ETICHETTA_STATO: Record<StatoAllenamento, string> = {
  programmato: 'Programmato',
  svolto: 'Svolto',
  annullato: 'Annullato',
};

export const ETICHETTA_PRESENZA: Record<StatoPresenza, string> = {
  presente: 'Presente',
  assente: 'Assente',
  infortunato: 'Infortunato',
};

/**
 * Colori e icone delle presenze, con i colori fissi degli stati del giocatore:
 * verde presente, arancio assente, rosso infortunato.
 */
export const COLORE_PRESENZA: Record<StatoPresenza, string> = {
  presente: 'bg-stato-disponibile text-white',
  assente: 'bg-stato-recupero text-black',
  infortunato: 'bg-stato-infortunato text-white',
};

export const ICONA_PRESENZA: Record<StatoPresenza, LucideIcon> = {
  presente: CheckIcon,
  assente: XIcon,
  infortunato: BandageIcon,
};

export interface AllenamentoInElenco {
  id: string;
  dataOra: string;
  luogo: string | null;
  stato: StatoAllenamento;
  presenti: number;
  rosa: number;
}

export interface PartitaStessoGiorno {
  id: string;
  dataOra: string;
  avversario: string;
  inCasa: boolean;
}

export interface RigaAppello {
  id: string;
  nome: string;
  cognome: string;
  numero: number | null;
  ruoloPreferito: RuoloGiocatore;
  presenza: StatoPresenza | null;
}

export interface DettaglioAllenamento {
  allenamento: {
    id: string;
    squadraStagioneId: string;
    dataOra: string;
    luogo: string | null;
    note: string | null;
    stato: StatoAllenamento;
  };
  rosa: RigaAppello[];
  partiteStessoGiorno: PartitaStessoGiorno[];
  presenze: number;
  log: number;
  /** Solo per chi legge i voti. */
  voti?: number;
}

export interface RigaVoti extends RigaAppello {
  dataNascita: string | null;
  voto: number | null;
}

export interface VotiAllenamento {
  allenamento: DettaglioAllenamento['allenamento'];
  rosa: RigaVoti[];
}

export interface OpzioniAllenamento {
  stagione: { dataInizio: string; dataFine: string };
  luoghi: string[];
}

export interface DataInAnteprima {
  dataOra: string;
  partite: PartitaStessoGiorno[];
  duplicato: boolean;
}

/** La riga sopra il titolo dell'allenamento: "mar 29/09/26", poi ora e luogo. */
export const formatoDataAllenamento = formatoIstante({
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
});

export const formatoRiga = formatoIstante({
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

export const formatoOra = formatoIstante({ hour: '2-digit', minute: '2-digit' });

/** Le parti del foglietto con la data nelle righe dell'elenco. */
export const formatoGiornoSettimana = formatoIstante({ weekday: 'short' });
export const formatoNumeroGiorno = formatoIstante({ day: 'numeric' });
export const formatoMeseBreve = formatoIstante({ month: 'short' });

/** Lo stato come si mostra: un programmato con il giorno passato e da chiudere. */
export type Segno = StatoAllenamento | 'daChiudere';

export const segnoDi = (a: Pick<AllenamentoInElenco, 'stato' | 'dataOra'>, oggi: string): Segno =>
  a.stato === 'programmato' && dataLocale(a.dataOra) < oggi ? 'daChiudere' : a.stato;

export const ETICHETTA_SEGNO: Record<Segno, string> = {
  ...ETICHETTA_STATO,
  daChiudere: 'Da chiudere',
};

/**
 * Fondo e testo del badge di stato. Svolto e da chiudere hanno i colori fissi
 * degli stati del giocatore (`--stato-*` in `src/index.css`): verde, e
 * arancio per cio che aspetta un gesto.
 */
export const COLORE_SEGNO: Record<Segno, string> = {
  programmato: 'bg-primary text-primary-foreground',
  svolto: 'bg-stato-disponibile text-white',
  annullato: 'bg-muted text-muted-foreground',
  daChiudere: 'bg-stato-recupero text-black',
};

/** Il pallino dello stesso colore, nel calendario e nella legenda. */
export const PALLINO_SEGNO: Record<Segno, string> = {
  programmato: 'bg-primary',
  svolto: 'bg-stato-disponibile',
  annullato: 'bg-muted-foreground',
  daChiudere: 'bg-stato-recupero',
};

export interface RiepilogoStagione {
  svolti: number;
  /** Programmati da oggi in avanti. */
  inProgramma: number;
  daChiudere: number;
  /** Presenti sulla rosa di tutti gli svolti, in percentuale; null senza svolti. */
  presenza: number | null;
}

/** I numeri della testata, sugli allenamenti di tutta la stagione. */
export function riepilogoStagione(
  elenco: readonly AllenamentoInElenco[],
  adesso: Date,
): RiepilogoStagione {
  const oggi = dataLocale(adesso);
  const r: RiepilogoStagione = { svolti: 0, inProgramma: 0, daChiudere: 0, presenza: null };
  let presenti = 0;
  let rosa = 0;
  for (const a of elenco) {
    const segno = segnoDi(a, oggi);
    if (segno === 'svolto') {
      r.svolti++;
      presenti += a.presenti;
      rosa += a.rosa;
    } else if (segno === 'programmato') r.inProgramma++;
    else if (segno === 'daChiudere') r.daChiudere++;
  }
  if (rosa > 0) r.presenza = Math.round((presenti / rosa) * 100);
  return r;
}

export interface GruppiAllenamenti {
  /** Programmati con il giorno gia passato: vanno segnati svolti o annullati. */
  daChiudere: AllenamentoInElenco[];
  /** Da oggi in avanti, dal piu vicino. */
  prossimi: AllenamentoInElenco[];
  /** Svolti e annullati passati, dal piu recente. */
  passati: AllenamentoInElenco[];
}

export function raggruppa(elenco: readonly AllenamentoInElenco[], adesso: Date): GruppiAllenamenti {
  const oggi = dataLocale(adesso);
  const perData = [...elenco].sort(
    (a, b) => new Date(a.dataOra).getTime() - new Date(b.dataOra).getTime(),
  );
  const gruppi: GruppiAllenamenti = { daChiudere: [], prossimi: [], passati: [] };
  for (const a of perData) {
    const giorno = dataLocale(a.dataOra);
    if (giorno >= oggi) gruppi.prossimi.push(a);
    else if (a.stato === 'programmato') gruppi.daChiudere.push(a);
    else gruppi.passati.push(a);
  }
  gruppi.passati.reverse();
  return gruppi;
}

export interface RiepilogoPresenze {
  presenti: number;
  assenti: number;
  infortunati: number;
  nonRilevati: number;
}

export function riepiloga(rosa: readonly { presenza: StatoPresenza | null }[]): RiepilogoPresenze {
  const r: RiepilogoPresenze = { presenti: 0, assenti: 0, infortunati: 0, nonRilevati: 0 };
  for (const g of rosa) {
    if (g.presenza === 'presente') r.presenti++;
    else if (g.presenza === 'assente') r.assenti++;
    else if (g.presenza === 'infortunato') r.infortunati++;
    else r.nonRilevati++;
  }
  return r;
}

/** "7 Rossi Mario" per le righe della rosa. */
export const nomeInRosa = (g: Pick<RigaAppello, 'numero' | 'nome' | 'cognome'>): string =>
  `${g.numero != null ? `${g.numero} ` : ''}${g.cognome} ${g.nome}`;

export interface GruppoSeduta {
  id: string;
  nome: string;
  diSistema: boolean;
  ruolo: string | null;
  ordine: number;
  giocatori: GiocatoreSeduta[];
}

export interface GiocatoreSeduta {
  id: string;
  nome: string;
  cognome: string;
  numero: number | null;
  ruoloPreferito: RuoloGiocatore;
}

export interface VoceMixBlocco {
  attributoId: string;
  nome: string;
  famiglia: string;
  quota: number;
}

export interface BloccoSeduta {
  id: string;
  gruppoIds: string[];
  /** La scheda o il test da cui nasce: l'uno o l'altro. */
  schedaId: string | null;
  testId: string | null;
  /** L'esecuzione del test fatta nel blocco, se c'è. */
  esecuzioneId: string | null;
  nome: string;
  rpeTarget: number;
  minutiProgrammati: number;
  ordine: number;
  mix: VoceMixBlocco[];
}

export interface ComposizioneSeduta {
  allenamento: DettaglioAllenamento['allenamento'];
  rosa: GiocatoreSeduta[];
  gruppi: GruppoSeduta[];
  blocchi: BloccoSeduta[];
}

/** Gruppo in cui sta il giocatore, o nessuno: un id non sta in due gruppi. */
export function gruppoDelGiocatore(
  gruppi: readonly { id: string; giocatori: readonly { id: string }[] }[],
  giocatoreId: string,
): string | null {
  return gruppi.find((g) => g.giocatori.some((j) => j.id === giocatoreId))?.id ?? null;
}

export function conteggioPiano(piano: {
  gruppi: readonly unknown[];
  blocchi: readonly unknown[];
}): {
  gruppi: number;
  blocchi: number;
} {
  return { gruppi: piano.gruppi.length, blocchi: piano.blocchi.length };
}

/** Cosa si perde eliminando: i voti solo a chi li legge. */
export function descrizioneEliminazione({
  presenze,
  voti,
  gruppi = 0,
  blocchi = 0,
  log = 0,
}: {
  presenze: number;
  voti?: number;
  gruppi?: number;
  blocchi?: number;
  log?: number;
}): string {
  const parti: string[] = [];
  if (presenze > 0) parti.push(presenze === 1 ? '1 presenza' : `${presenze} presenze`);
  if (voti === undefined) parti.push('gli eventuali voti');
  else if (voti > 0) parti.push(voti === 1 ? '1 voto' : `${voti} voti`);
  if (gruppi > 0) parti.push(gruppi === 1 ? '1 gruppo' : `${gruppi} gruppi`);
  if (blocchi > 0) parti.push(blocchi === 1 ? '1 blocco' : `${blocchi} blocchi`);
  if (log > 0) {
    parti.push(log === 1 ? '1 riga di Registro seduta' : `${log} righe di Registro seduta`);
  }
  return parti.length > 0
    ? `Insieme all'allenamento si cancellano ${parti.join(' e ')}.`
    : 'Non ci sono presenze, voti, gruppi, blocchi né righe di Registro seduta.';
}

export interface VoceMixLog {
  attributoId: string;
  nome: string;
  famiglia: string;
  quota: number;
}

export interface RigaLog {
  giocatoreId: string;
  minutiEseguiti: number;
  rpeEseguito: number;
  motivo: string | null;
  scostamento: number;
  lettura: EtichettaScostamento;
  motivoVisibile: boolean;
  doseUa: number;
  carichi: { attributoId: string; centesimiUa: number }[];
  audit: boolean;
}

export interface BloccoLog {
  bloccoId: string;
  nome: string;
  rpeTarget: number;
  minutiProgrammati: number;
  ordine: number;
  mix: VoceMixLog[];
  classifica: ClassificaBlocco;
  giocatoriAlert: string[];
  log: RigaLog[];
}

export interface GiocatoreLog {
  id: string;
  nome: string;
  cognome: string;
  numero: number | null;
  ruoloPreferito: RuoloGiocatore;
  doseTotaleUa: number;
}

export interface GruppoLog {
  id: string;
  nome: string;
  diSistema: boolean;
  ruolo: RuoloGiocatore | null;
  ordine: number;
  giocatori: GiocatoreLog[];
  blocchi: BloccoLog[];
}

export interface LogSeduta {
  allenamento: DettaglioAllenamento['allenamento'];
  contaNeiCalcoli: boolean;
  gruppi: GruppoLog[];
}

export function rigaLogDi(blocco: BloccoLog, giocatoreId: string): RigaLog | undefined {
  return blocco.log.find((l) => l.giocatoreId === giocatoreId);
}

/** Valore di coda di una cella: minuti e RPE in un solo string. */
export function valoreLog(minuti: number, rpe: number): string {
  return `${minuti}:${rpe}`;
}

export function chiaveLog(bloccoId: string, giocatoreId: string): string {
  return `${bloccoId}:${giocatoreId}`;
}

export function daValoreLog(valore: string | null): { minuti: number; rpe: number } | null {
  if (!valore) return null;
  const parti = valore.split(':');
  if (parti.length !== 2) return null;
  const minuti = Number(parti[0]);
  const rpe = Number(parti[1]);
  if (!Number.isInteger(minuti) || minuti < 0 || !Number.isInteger(rpe) || rpe < 1 || rpe > 10) {
    return null;
  }
  return { minuti, rpe };
}

export function celleDaLog(gruppi: readonly GruppoLog[]): Record<string, string | null> {
  const celle: Record<string, string | null> = {};
  for (const g of gruppi) {
    for (const j of g.giocatori) {
      for (const b of g.blocchi) {
        const r = rigaLogDi(b, j.id);
        celle[chiaveLog(b.bloccoId, j.id)] = r ? valoreLog(r.minutiEseguiti, r.rpeEseguito) : null;
      }
    }
  }
  return celle;
}

/** Presenze, voti o Registro seduta: il giorno dell'allenamento non si cambia più. */
export function giornoBloccato(d: { presenze: number; voti?: number; log: number }): boolean {
  return d.presenze + (d.voti ?? 0) + d.log > 0;
}

export const ETICHETTA_LETTURA: Record<EtichettaScostamento, string> = {
  in_linea: 'In linea',
  sotto: 'Sotto',
  sopra: 'Sopra',
};

/**
 * La voce «Risultati» di un blocco nato da un test: dove porta (l'esecuzione
 * già salvata o quella nuova dai presenti), o perché è spenta. Un blocco da
 * scheda non ce l'ha.
 */
export function risultatiDelBlocco(
  ss: string,
  stato: StatoAllenamento,
  b: Pick<BloccoSeduta, 'id' | 'testId' | 'esecuzioneId'>,
): { a: string } | { motivo: string } | null {
  if (!b.testId) return null;
  if (stato === 'annullato') return { motivo: 'L’allenamento è annullato.' };
  if (stato !== 'svolto') return { motivo: 'Dopo Chiudi appello, per i presenti.' };
  return {
    a: b.esecuzioneId
      ? `/sq/${ss}/test/${b.testId}/esecuzioni/${b.esecuzioneId}`
      : `/sq/${ss}/test/${b.testId}/esegui?blocco=${b.id}`,
  };
}
